import {
  SideNavigationV2Props,
  PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import {
  BuildLogSchema,
  type BuildLog,
} from "skedyul-core/features/workflows/resources/types";
import type { DeploymentDetail } from "skedyul-core/features/deployments/resources/types";
import { store, type RootState } from "@store/index";
import type { ButtonProps } from "skedyul-ui/ui";

type DeploymentLogsParams = {
  deploymentId: string;
};

export const deploymentLogsSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }> & {
    props: DeploymentLogsParams;
  },
): SideNavigationV2Props<PageV2Component> => {
  const { sideNavigation } = args.state;
  const paramsDeploymentId = sideNavigation[args.position]?.params
    ?.deploymentId as string | undefined;
  const deploymentId = args.props.deploymentId || paramsDeploymentId || "";

  const isOpen = sideNavigation[args.position]?.isOpen ?? true;

  // Get deployment data from args.state (passed from layout) or fall back to store
  // This ensures we get reactive updates when Redux state changes
  const fullState = args.state as RootState;
  const stateDeployment = fullState.deployments?.deployment?.data ?? null;
  const stateLogs = fullState.deployments?.deployment?.logs ?? null;

  // Also check store.getState() for the most current data (similar to workflow run schema)
  const liveState = store.getState();
  const liveDeployment = liveState.deployments?.deployment?.data ?? null;
  const liveLogs = liveState.deployments?.deployment?.logs ?? null;

  // Use live state if available (for real-time updates), otherwise fall back to passed state
  const deployment = liveDeployment ?? stateDeployment;
  const logs = liveLogs ?? stateLogs;

  // Only use data if it matches the current deploymentId
  const matchesCurrentId = deployment?.id === deploymentId;
  const currentDeployment: DeploymentDetail | null = matchesCurrentId
    ? deployment
    : null;
  const currentLogs: BuildLog | null = matchesCurrentId ? logs : null;

  const logsError = currentDeployment?.logsError ?? null;

  // Validate build logs if present
  let validatedBuildLogs: BuildLog | null | undefined = null;
  if (currentLogs) {
    try {
      validatedBuildLogs = BuildLogSchema.parse(currentLogs);
    } catch {
      // If parsing fails, pass null to show error message in component
      validatedBuildLogs = null;
    }
  }

  const displayBuildLogs =
    validatedBuildLogs?.length || !logsError
      ? validatedBuildLogs
      : [
          {
            timestamp: new Date().toISOString(),
            level: "error",
            message: `Could not load logs: ${logsError}`,
          },
        ];

  // Build title from deployment info
  const appName =
    currentDeployment?.appName ?? currentDeployment?.appHandle ?? "";
  const branch = currentDeployment?.branch ?? "";
  const commitSha = currentDeployment?.commitSha?.slice(0, 7) ?? "";
  const statusLabel = currentDeployment?.status
    ? ` (${currentDeployment.status.charAt(0).toUpperCase() + currentDeployment.status.slice(1)})`
    : "";

  // Build a descriptive title
  let title = "Deployment Logs";
  if (appName) {
    title = `${appName}${statusLabel}`;
  } else if (deploymentId) {
    title = `Deployment${statusLabel}`;
  }

  // Build subtitle from branch and commit
  const subtitle =
    branch && commitSha
      ? `${branch} • ${commitSha}`
      : branch || commitSha || `ID: ${deploymentId.slice(0, 12)}...`;

  // Build logs URL if we have the necessary IDs
  const subdomain = args.routerContext.params?.subdomain;
  const appId = currentDeployment?.appId;
  const appVersionId = currentDeployment?.appVersionId;

  // Build the logs URL with app and appVersion filters
  let logsUrl: string | undefined;
  if (subdomain && appId && appVersionId) {
    logsUrl = `/${subdomain}/developers/logs?app=${appId}&appVersion=${appVersionId}`;
  }

  // Build components array
  const components: any[] = [];

  // Add View Provision Logs link if this deployment is for a default app version (triggers provision)
  // The deployment triggers a provision when it's deploying to the default app version
  if (logsUrl && currentDeployment?.liveAppVersion) {
    components.push({
      type: "Card" as const,
      props: {
        variant: "outline",
        className: "mb-4",
        header: {
          title: "Provision Logs",
          description:
            "This deployment triggered a provision to the default app version",
        },
        footer: {
          actions: [
            {
              label: "View Provision Logs",
              leftIcon: "FileText",
              variant: "outline",
              size: "sm",
              href: logsUrl,
            } as ButtonProps,
          ],
        },
      },
    });
  }

  // Add the build logs terminal
  components.push({
    type: "BuildLogsTerminal" as const,
    title: subtitle,
    props: {
      buildLogs: displayBuildLogs,
      label: "Build Output",
      expandable: true,
      defaultExpanded: true,
    },
  });

  return {
    side: args.position,
    isOpen,
    width: args.width || "750px",
    component: {
      component: "PageV2",
      props: {
        title,
        content: {
          structure: "sidebar",
          components,
        },
      },
    },
  };
};
